// HomeScreen

/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native"
import { useUser } from "@/queries/user"
import useGreeting from "@/hooks/useGreeting"
import { useAnalysisStats } from "@/queries/analysis"
import AnalyzeWidget from "@/components/widgets/AnalyzeWidget"
import LoansWidget from "@/components/widgets/LoansWidget"
import PropertiesWidget from "@/components/widgets/PropertiesWidget"
import RewardsWidgetV2 from "@/components/widgets/RewardsWidgetV2"
import * as HomeEvents from "@/analytics/events/home"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { requestTrackingPermissions } from "@/utils/TrackingUtils"
import PreApprovalWidget from "@/components/widgets/PreApprovalWidget"
import BFText, { H2 } from "@/components/standard/BFText"
import { router } from "expo-router"
import { MarketingWidget } from "@/components/widgets/MarketingWidget"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useLatestMarketingWidget } from "@/queries/marketingwidget"
import AccountIcon from "@/assets/icons/account-filled.svg"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import FullScreenBackgroundGradient from "@/components/backgrounds/FullScreenBackgroundGradient"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import React, { useMemo } from "react"
import { getUtm } from "@/stores/utm"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import AppDownloadWidget from "@/components/widgets/AppDownloadWidget"
import { EventWidget } from "@/screens/events/EventWidget"

export default function HomeScreen() {
  const { data } = useAnalysisStats()
  const insets = useSafeAreaInsets()
  const isDesktop = useIsDesktop()
  const isFirstTimeUser: boolean = data?.userType === "firstTimeUser"
  const { isWeb } = useDeviceAttributes()
  const isEventSource =
    getUtm().utmSource?.toLocaleLowerCase() === "event" && isWeb

  const {
    data: marketingData,
    isLoading: isLoadingMarketing,
    isError: isMarketingError,
  } = useLatestMarketingWidget()
  const { data: user, isLoading, isError } = useUser()

  useViewedCallback(() => {
    setStatusBarStyle("light")
    if (!isLoadingMarketing && !isMarketingError) {
      HomeEvents.homeViewed(marketingData?.heading)
    }

    if (Platform.OS === "ios") {
      void (async () => {
        await requestTrackingPermissions()
      })()
    }
  }, [marketingData, isLoadingMarketing, isMarketingError])

  const { greeting } = useGreeting()
  const userFirstName = user?.me?.firstName || "Guest"
  const personalizedGreeting = useMemo(() => {
    return isFirstTimeUser
      ? `Welcome, ${userFirstName}`
      : `${greeting}, ${userFirstName}`
  }, [isFirstTimeUser, greeting, userFirstName])

  if (isLoading || isLoadingMarketing || isMarketingError || isError) {
    return (
      <>
        <FullScreenBackgroundGradient />
        <ActivityIndicator
          className="items-center justify-center flex-1 pt-10"
          size={"small"}
        />
      </>
    )
  }

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <BFScreen
      noBackButton={true}
      headerSticky={false}
      dark={true}
      gradientBackground={true}
      includeStandardSideMargins={true}
      ignoreTopSafeArea={true}
    >
      <View className="flex-1 web:flex web:flex-col">
        <ScrollView>
          <View style={{ marginTop: insets.top + 15 }} className="items-center">
            <View className="flex-row items-start justify-between text-white">
              <View className="flex-1">
                <GreetingText textText={personalizedGreeting} />
              </View>

              {!isDesktop && (
                <Pressable
                  testID="tab-account"
                  className="pt-[11px] web:ml-6"
                  onPress={() => {
                    router.push("/account")
                  }}
                >
                  <AccountIcon width={24} height={24} />
                </Pressable>
              )}
            </View>

            <BFText className="text-gray-300 font-spacegrotesk text-[12px] leading-4 mb-5">
              Today, {formattedDate}
            </BFText>
          </View>

          {isEventSource && (
            <View className="items-center justify-center">
              <AppDownloadWidget />
            </View>
          )}

          {/* GRID WIDGETS */}
          <View className="flex-row flex-wrap items-start justify-center text-white">
            <AnalyzeWidget />
            <PropertiesWidget />
            <LoansWidget />
            <RewardsWidgetV2 />
          </View>

          {/* FULL WIDTH WIDGETS */}
          <View className="items-center justify-center mb-4">
            <EventWidget />
            <MarketingWidget
              isLoading={isLoadingMarketing}
              marketingData={marketingData || null}
            />
            <PreApprovalWidget />
          </View>
        </ScrollView>
      </View>
    </BFScreen>
  )
}

interface GreetingTextProps {
  textText: string
}

const GreetingText: React.FC<GreetingTextProps> = ({
  textText,
}: GreetingTextProps) => {
  return (
    <H2 className={`min-h-6 text-white text-left my-3`} testID="greeting-text">
      {textText}!
    </H2>
  )
}
